<script setup lang="ts">
import { computed, watch, onMounted } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useCharacterDataStore } from "@/stores/character-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { useAuthDataStore } from "@/stores/auth-data";
import { useGuidedElementPicker } from "@/composables/useGuidedElementPicker";
import ElementBrowser from "@/components/atoms/swade/ElementBrowser.vue";
import GuidedScopeFilters from "@/components/atoms/element-filters/GuidedScopeFilters.vue";
import FilterControl from "@/components/atoms/element-filters/FilterControl.vue";
import EdgeTypeFilter from "@/components/atoms/element-filters/EdgeTypeFilter.vue";
import { buildSecondaryChips, clearSecondaryChip } from "@/composables/useElementPredicates";
import type { SwadeCharacterInterface } from "@/types/characters";
import type { SwadeEdgeInterface, SwadeEdgeContainerInterface, SettingElementInterface } from "@/types/items";
import type { ColumnConfig, FilterChip } from "@/types/browser";
import { containerDisplayNameFor } from '@/appdata/chosen-labels';
import { useTrappingEntries } from '@/composables/useTrappingEntries';
import TrappingEntryField from '@/components/atoms/swade/TrappingEntryField.vue';

const props = defineProps<{ stepIndex: number; }>();
const character = defineModel<SwadeCharacterInterface | null>('character');

const ux = useUxData();
const auth = useAuthDataStore();
const characterData = useCharacterDataStore();
const setupStore = useGuidedSetupStore();

const {
  browserRef, open, onSelect,
  dataItems, selectedElement, loading, metadata, links,
  onSearch, onNextPage, onPreviousPage, filters,
  showWorldFilter, filtersActive, clearFilters,
} = useGuidedElementPicker('edges', {
  alias: 'advantages',
  character: () => character.value,
});

const edges = computed(() => character.value?.edges ?? []);
// An edge whose trapping modification leaves the value to the player shows TrappingEntryField on
// its card (spec 2026-10-05). The field needs no handler here: the store write rebuilds the
// overrides itself, and a trapping changes no perk or build number.
const { entryFor: trappingEntryFor } = useTrappingEntries(() => character.value, 'container.edges');
const perkPointsTotal = computed(() => characterData.build?.perkPoints?.total ?? 0);
const perkPointsAllocated = computed(() => characterData.build?.perkPoints?.allocated ?? 0);
const perkPointsRemaining = computed(() => perkPointsTotal.value - perkPointsAllocated.value);

watch(() => true, () => { setupStore.validateStep(props.stepIndex, true); }, { immediate: true });

// Widened to SettingElementInterface to match ElementBrowser's isAdded/canAdd prop types; every
// row this picker can show or add is an edge (see onAdd below), so allowMultiple is asserted
// rather than narrowed.
const isEdgeAdded = (edge: SettingElementInterface): boolean =>
  edges.value.some((c: SwadeEdgeContainerInterface) => c.selection.uuid === edge.uuid);
const isAdded = (edge: SettingElementInterface) => isEdgeAdded(edge);
const canAdd = (edge: SettingElementInterface) => !isEdgeAdded(edge) || ((edge as SwadeEdgeInterface).allowMultiple ?? false);

// The picker only ever offers edges (useGuidedElementPicker('edges', ...) fetches the advantages
// collection); ElementBrowser's #details slot types its element generically. Same truthiness the
// v-if already tested.
const isEdge = (element: SettingElementInterface | null | undefined): element is SwadeEdgeInterface => !!element;

const getEdgeTypeLabel = (type: string | null): string => {
  if (!type) return '';
  return ux.labels?.swade?.data?.edges?.types?.[type] ?? type;
};

const columns = computed<ColumnConfig[]>(() => [
  { field: 'name', header: ux.labels?.swade?.data?.edges?.name ?? 'Name',
    columnClass: ['name-column'] },
  { field: 'type', header: ux.labels?.swade?.data?.edges?.category ?? 'Category', formatter: (el: any) => getEdgeTypeLabel(el.type) },
  { field: 'requirements', header: ux.labels?.swade?.data?.edges?.requirements ?? 'Requirements', formatter: (el: any) => el.requirements ?? '' },
  { field: 'source.name', header: ux.labels?.swade?.data?.edges?.source ?? 'Source', formatter: (el: any) => el.source?.name ?? '' },
]);

const edgeTypes = computed<string[]>({
  get: () => (filters.value as any).types ?? [],
  set: (value) => { (filters.value as any).types = value; },
});

// Chips live with their controls: SourceFilterControl owns the source chip, and the
// category chips come from the shared per-type mapping the data lists and the world
// browser read, so the wording matches everywhere.
const categoryChips = computed<FilterChip[]>(() => buildSecondaryChips('edges', filters.value as any));
const removeCategory = (chip: FilterChip) => clearSecondaryChip('edges', filters.value as any, chip);

const onAdd = async (edge: SettingElementInterface) => {
  if (!character.value) return;
  await characterData.addEdgeToCharacter(character.value, edge as SwadeEdgeInterface);
  characterData.updateAllocatedPerkPoints(character.value);
};
const onRemoveEdge = async (container: SwadeEdgeContainerInterface) => {
  if (!character.value) return;
  await characterData.removeEdge(character.value, container);
  characterData.updateAllocatedPerkPoints(character.value);
};
const onRemoveSelectedEdge = async () => {
  if (!selectedElement.value || !character.value) return;
  const container = edges.value.find((c: SwadeEdgeContainerInterface) => c.selection.uuid === selectedElement.value?.uuid);
  if (container) await onRemoveEdge(container);
};

onMounted(() => { setupStore.validateStep(props.stepIndex, true); });
</script>

<template>
  <div class="character-edges-step">
    <div class="form-group mb-3">
      <label class="form-label fw-semibold" v-html="ux.labels?.guidedSetup?.character?.edgesLabel"></label>
      <div class="d-flex">
        <span class="icon mb-auto me-2 flex-grow-0"><span class="fa-light fa-info-circle me-1"></span></span>
        <p class="form-text mt-0 mb-2 flex-grow-1" v-html="ux.labels?.guidedSetup?.character?.edgesHelp"></p>
      </div>

      <div v-if="perkPointsTotal > 0"
           class="perk-points-tracker alert d-flex justify-content-between align-items-center mb-3"
           :class="perkPointsRemaining < 0 ? 'alert-danger' : 'alert-info'">
        <span>
          <span class="fa-light fa-star me-2"></span>
          <span v-html="ux.labels?.guidedSetup?.character?.perkPointsAvailable"></span>
        </span>
        <span class="badge fs-6" :class="perkPointsRemaining < 0 ? 'bg-danger' : 'bg-primary'">{{ perkPointsRemaining }}</span>
      </div>

      <div v-if="edges.length > 0" class="edge-list d-flex flex-md-row flex-column flex-fill flex-wrap gap-2 mb-3">
        <!-- :key is required, not hygiene: keyed, a card's TrappingEntryField moves with it when
             the list reorders; unkeyed, Vue would hand the instance another card's props and a
             half-typed answer would be dropped. -->
        <div v-for="container in edges" :key="container.uuid ?? container.selection.uuid" class="card card-body edge-item">
          <div class="d-flex justify-content-between align-items-center">
            <!-- Unlike Edges.vue, this step has no separate view/edit mode — every edge shown here
                 is being built. Raw catalog name while the trapping entry field shows, so the
                 trapping is never rendered twice; the composed, override-aware containerDisplayName
                 label otherwise. -->
            <div><span class="edge-title fw-semibold">{{ trappingEntryFor(container) !== null ? container.selection.name : containerDisplayNameFor(character, container) }}</span></div>
            <button type="button" class="btn btn-sm btn-link text-danger" @click="onRemoveEdge(container)">
              <span class="fa-light fa-times"></span>
            </button>
          </div>
          <!-- On its own row and full width, unlike the sheet's inline 12rem field: a card here is a
               fifth of the list's width at lg, narrower than the field. d-flex and w-100 are
               !important utilities, so they win over the .trapping-entry rules. -->
          <div v-if="trappingEntryFor(container)" class="trapping-entry d-flex my-2">
            <TrappingEntryField class="w-100"
                                data-test="edge-trapping-entry"
                                :character="character!"
                                :container="container"
                                :effect="trappingEntryFor(container)!"
            />
          </div>
          <span v-if="auth.user?.subscriber" class="edge-description" v-html="container.selection.summary"></span>
        </div>
      </div>

      <button type="button" class="btn btn-primary w-100 d-lg-none mb-3 add-trigger" @click="open()">
        <span class="fa-light fa-plus"></span>
        {{ ux.labels?.guidedSetup?.character?.addEdges ?? 'Add Edges' }}
      </button>

      <ElementBrowser
        ref="browserRef"
        :inline="true"
        :title="ux.labels?.guidedSetup?.character?.addEdges ?? 'Add Edges'"
        :data-items="dataItems"
        :columns="columns"
        :selected-element="selectedElement"
        :is-added="isAdded"
        :can-add="canAdd"
        :loading="loading"
        :metadata="metadata"
        :links="links"
        :filters-active="filtersActive"
        @add="onAdd"
        @select="onSelect"
        @search="onSearch"
        @next="onNextPage"
        @previous="onPreviousPage"
        @clear="clearFilters"
      >
        <template #filters>
          <GuidedScopeFilters :show-world="showWorldFilter" />
          <FilterControl
            :label="ux.labels?.swade?.data?.edges?.category ?? 'Category'"
            :chips="categoryChips"
            :multiple="true"
            @remove="removeCategory"
          >
            <EdgeTypeFilter
              v-model:types="edgeTypes"
              :label="ux.labels?.swade?.data?.filters?.labels?.edgeType ?? 'Category'"
              :placeholder="ux.labels?.swade?.data?.filters?.category ?? 'Category'"
            />
          </FilterControl>
        </template>

        <template #details="{ element }">
          <template v-if="isEdge(element)">
            <div v-if="element.requirements" class="small text-muted mb-2">
              <strong class="element-property-name" v-html="ux.labels?.swade?.data?.edges?.requirements"></strong>
              {{ element.requirements }}
            </div>
            <div v-if="element.summary" class="card-text small mb-3" v-html="element.summary"></div>
            <button
              v-if="isEdgeAdded(element)"
              type="button"
              class="btn btn-outline-danger btn-sm"
              @click="onRemoveSelectedEdge"
              v-html="ux.labels?.guidedSetup?.character?.removeEdge ?? 'Remove Edge'"
            ></button>
          </template>
        </template>
      </ElementBrowser>
    </div>
  </div>
</template>
